/**
 * Responsibility: a stack of card images shown one at a time.
 *
 * A saved search is a phrase, and a list of phrases is a poor way to
 * recognise the one you want - "charizard" could be four saved searches
 * with different filters. The cards it found are what make it recognisable.
 *
 * It rotates rather than showing a row of thumbnails because the row would
 * have to be tiny to fit a table cell, and four unreadable stamps are worse
 * than one readable card.
 *
 * Every instance advances off the same shared tick rather than its own
 * timer, so a list of ten does not turn into ten interleaved animations
 * firing at ten different moments.
 */
import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { CardThumb } from '../grading/CardThumb';

/** Slow enough to read, quick enough to get through four in a glance. */
const STEP_MS = 2600;

/** Matched to the crossfade in the stylesheet below. */
const FADE_MS = 320;

/**
 * One timer for the whole page.
 *
 * Each mounted thumb subscribes; the interval only runs while at least one
 * is on screen. Without this, a table of saved searches starts a timer per
 * row and they drift apart within a minute.
 */
const listeners = new Set<(tick: number) => void>();
let timer: ReturnType<typeof setInterval> | null = null;
let shared = 0;

function subscribe(fn: (tick: number) => void): () => void {
  listeners.add(fn);
  if (timer === null) {
    timer = setInterval(() => {
      shared += 1;
      for (const listener of listeners) listener(shared);
    }, STEP_MS);
  }
  return () => {
    listeners.delete(fn);
    if (listeners.size === 0 && timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };
}

type RotatingThumbProps = {
  images: string[];
  alt: string;
  width: number;
};

export function RotatingThumb({ images, alt, width }: RotatingThumbProps) {
  const [tick, setTick] = useState(shared);

  useEffect(() => {
    // A single image has nothing to rotate to, and subscribing it would
    // keep the page's timer alive for no reason.
    if (images.length < 2) return undefined;
    return subscribe(setTick);
  }, [images.length]);

  if (images.length === 0) {
    return <CardThumb src={null} alt={alt} width={width} />;
  }

  const index = tick % images.length;

  return (
    <Box sx={{ position: 'relative', width, flexShrink: 0 }}>
      <CardThumb
        // Keyed on the URL so React swaps the element rather than mutating
        // src in place, which is what lets the fade run at all.
        key={images[index]}
        src={images[index]}
        alt={alt}
        width={width}
      />
      {images.length > 1 ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            animation: `thumbIn ${FADE_MS}ms ease`,
            '@keyframes thumbIn': { from: { opacity: 0.25 }, to: { opacity: 1 } },
            pointerEvents: 'none',
          }}
        />
      ) : null}
    </Box>
  );
}
